// Reading-selection contract (docs/corpus-schema.md §3, §8): the input schema
// for POST /api/recommend and the candidate shape it returns. This is the
// union of the two source implementations' contracts — the monorepo's caps +
// dedup and the app's options — plus the §2 layering revision: the publication
// scope is a per-request filter the caller supplies, layered on the store's
// `recommendable` floor, replacing the ALLOWED_PUBLICATION_PATTERNS global.

// Namespace import, not `import { z }` — zod ships CJS for the node condition
// and the named form resolves to `undefined` under vitest's multi-project run
// (same as ./catalog.ts).
import * as z from "zod";

/**
 * The vocabulary artifact file each generation is published as, one entry per
 * generation. This is the whole of §4's naming seam, resolved (2026-09-22):
 * **the store's date string is the name** — `2025-12-21` is what
 * `vocab_version` holds, what the guards compare, and what the pipeline
 * stamps — and the artifact's `v1` is a *file name*, not a second version
 * scheme. Keeping the two in one map means a bump is one entry here rather
 * than a rename nobody can coordinate: the CDN file is never renamed, because
 * a rename breaks every client that has not shipped the new build.
 *
 * What an entry asserts, and what it does not. It asserts a *name*: clients
 * built for this generation fetch this file. It does NOT assert that the
 * file's word_id layout is the store's. Kanban #55 diffed
 * `vocabulary_4096_v1.json` against `production_texts.vocab` (generation
 * 2025-12-21) and found the same 4096 words at the same word_id for only 519
 * of them (12.7%). The JSON's order is the canonical one (the model's;
 * docs/vocab-order-forensics.md § Decision, 2026-09-23, reversing #55's
 * presumption), and the store is renumbered to it by #166. Until that lands,
 * a reader mask built from the file is *labelled* 2025-12-21 and passes §4's
 * `vocab_version` predicate while its tied-word bits point at different words
 * than the store's masks do. The type below guards the name — no generation
 * goes active without a published artifact — and nothing here guards the
 * layout: that is #55's audit, and #166 is the open action that closes it.
 *
 * Only the generations with a published artifact belong here. 1.0.0 and 1.1.0
 * existed in the store (§4) but no artifact of this shape was published for
 * them, and no surviving mask carries them.
 */
const VOCAB_ARTIFACT_BY_VERSION = {
  "2025-12-21": "vocabulary_4096_v1.json",
} as const satisfies Record<string, string>;

/** A vocabulary generation that has a published artifact. */
export type CorpusVocabVersion = keyof typeof VOCAB_ARTIFACT_BY_VERSION;

/**
 * The vocabulary generation this build's clients name their artifact by. The
 * selection queries in corpus-db do not read it: they filter on the store's
 * own generation (`queryStoreGeneration`), so a build serves a store before
 * and after a renumber.
 *
 * Typed as a key of the artifact map on purpose: a generation string with no
 * published artifact cannot be made active without someone noticing. Note
 * the migration journal does not read this: 0001's backfill stamps the
 * literal `'2025-12-21'`, because a migration is frozen at the moment it was
 * written and must not follow a later bump.
 */
export const CORPUS_VOCAB_VERSION: CorpusVocabVersion = "2025-12-21";

/**
 * The vocabulary artifact file name for a generation — the one place the
 * client-side `v1` spelling is written down. Callers build a URL from it
 * (`reader/vocabulary-loader.ts`) rather than repeating the string.
 */
export function corpusVocabArtifactName(
  version: CorpusVocabVersion = CORPUS_VOCAB_VERSION,
): string {
  return VOCAB_ARTIFACT_BY_VERSION[version];
}

/**
 * `word_id` is a bit *position*: bit n of `common_word_bits` is word n,
 * 1-indexed, so the mask covers word_ids 1..4096 and the vocabulary's tail
 * (OOV insertions past 4096) is counted by `word_text` but never masked.
 */
export const CORPUS_VOCAB_SIZE = 4096;

/**
 * Target known-word percentage for a recommended reading. Phase 1 prefilters
 * on it (share of a text's common words the reader knows) and phase 3 scores
 * deviation from it.
 */
export const OPTIMAL_KNOWN_PERCENT = 90;

/**
 * Phase 2 returns `20 + limit * this` candidates so phase 3 has a pool to
 * re-rank with confidence weighting before cutting to `limit`.
 */
export const PHASE_2_LIMIT_MULTIPLIER = 3;

// Caps on caller-supplied arrays / strings (ported verbatim from the
// monorepo's SelectionInputSchema — the API is an authenticated but
// network-reachable surface feeding these values into unnest/ANY/NOT IN
// clauses, so uncapped arrays are cost amplification). Tuned generously above
// the largest legitimate vocabulary profile.
const MAX_WORD_IDS = CORPUS_VOCAB_SIZE;
export const MAX_EXTRA_WORDS = 2000;
export const MAX_WORD_LENGTH = 64;
export const MAX_EXCLUDE_TEXT_IDS = 1000;
const MAX_SCOPE_PATTERNS = 20;
const MAX_SCOPE_PATTERN_LENGTH = 255;
const MAX_SCOPE_PUBLICATION_IDS = 200;

/**
 * Per-request publication scope (§2): a consumer that wants recommendations
 * from only certain publications passes title patterns (SQL LIKE syntax —
 * `%`/`_` are wildcards; literal ones must be escaped by the caller) and/or
 * publication ids. Applied on top of the store's `recommendable` floor, never
 * instead of it. An omitted scope means the whole recommendable universe.
 * The two legacy ALLOWED_PUBLICATION_PATTERNS ("500 %", "Poetry %") become a
 * scope the porting consumer passes, not a rule this contract imposes.
 */
export const publicationScopeSchema = z.object({
  titlePatterns: z
    .array(z.string().min(1).max(MAX_SCOPE_PATTERN_LENGTH))
    .max(MAX_SCOPE_PATTERNS)
    .optional(),
  publicationIds: z.array(z.uuid()).max(MAX_SCOPE_PUBLICATION_IDS).optional(),
});

export type PublicationScope = z.infer<typeof publicationScopeSchema>;

export const selectionOptionsSchema = z
  .object({
    /**
     * Floor on the share of a text's words the reader must already know.
     *
     * Bounded from below by the query, not by this schema: phase 1 prefilters
     * candidates at `OPTIMAL_KNOWN_PERCENT` (share of a text's COMMON words
     * known) before phase 3 applies this value to the full word count, so
     * lowering it past 90 widens the pool far less than the number suggests —
     * phase 1 has already dropped what it would have admitted. Verbatim from
     * the sources (§3), recorded here because the interaction is invisible to
     * a caller reading only this field.
     */
    minKnownPercent: z.number().min(0).max(100).default(80),
    maxKnownPercent: z.number().min(0).max(100).default(95),
    limit: z.number().int().min(1).max(100).default(20),
    /**
     * `text.id` UUIDs to exclude (already-read or already-offered rows).
     *
     * Validated as UUIDs, not as bounded strings: these are bound into
     * `t.id NOT IN (…)` against a `uuid` column, so Postgres infers each
     * parameter as `uuid` and a slug or an `int_id` fails in the database as
     * `invalid input syntax for type uuid` — a 500 out of a caller error. Same
     * reasoning as the route's pre-validation of `/api/text/:id`.
     */
    excludeTextIds: z.array(z.uuid()).max(MAX_EXCLUDE_TEXT_IDS).default([]),
    requireKnownTitleWords: z.boolean().default(false),
    publicationScope: publicationScopeSchema.optional(),
  })
  // An inverted window is a caller bug that the query cannot report: it
  // becomes `BETWEEN 95 AND 80`, which no row satisfies, and the caller gets
  // an ordinary empty list with nothing to distinguish it from a genuinely
  // empty universe. Checked after defaults resolve, so supplying only one
  // side is checked against the other's default.
  .refine((options) => options.minKnownPercent <= options.maxKnownPercent, {
    message: "minKnownPercent must be less than or equal to maxKnownPercent",
    path: ["minKnownPercent"],
  });

export type SelectionOptions = z.input<typeof selectionOptionsSchema>;

/** A capped list of 1-indexed word_ids, each a bit position in 1..4096. */
export const wordIdsSchema = z
  .array(z.number().int().min(1).max(CORPUS_VOCAB_SIZE))
  .max(MAX_WORD_IDS);

/**
 * The reader's vocabulary profile as a caller sends it: the fields and caps
 * every recommender route shares (this one and `passages.ts`'s). A shape, not
 * a schema, so each route spreads it into its own object and keeps its own
 * refinements — zod refuses to `.extend` an object that carries one.
 */
export const vocabularyProfileShape = {
  /** 1-indexed word_ids the reader was assessed on (weight 1.0). */
  assessedWordIds: wordIdsSchema,
  /** 1-indexed word_ids from prediction, excluding assessed ones. */
  predictedWordIds: wordIdsSchema,
  /** Model confidences (0–1), parallel to predictedWordIds. */
  predictedConfidences: z.array(z.number().min(0).max(1)).max(MAX_WORD_IDS),
  /** Known words OUTSIDE the 4096 vocabulary, resolved server-side. */
  extraKnownWords: z
    .array(z.string().max(MAX_WORD_LENGTH))
    .max(MAX_EXTRA_WORDS),
};

/** The refinement every profile carries: confidences parallel their ids. */
export function withParallelPredictions<
  T extends z.ZodType<{
    predictedWordIds: number[];
    predictedConfidences: number[];
  }>,
>(schema: T): T {
  return schema.refine(
    (data) => data.predictedWordIds.length === data.predictedConfidences.length,
    {
      message:
        "predictedConfidences array length must match predictedWordIds length",
      path: ["predictedConfidences"],
    },
  );
}

export const selectionInputSchema = withParallelPredictions(
  z.object({
    ...vocabularyProfileShape,
    options: selectionOptionsSchema.prefault({}),
  }),
);

export type SelectionInput = z.infer<typeof selectionInputSchema>;

/** The wire shape a caller sends — options may be partial or absent. */
export type SelectionRequest = z.input<typeof selectionInputSchema>;

export interface PublicationMetadata {
  id: string;
  title: string;
  coverImage: string | null;
  author: string | null;
}

/**
 * One ranked recommendation. Deliberately bodiless (§3): the ranking row
 * carries metadata and stats only — the text body is served by
 * GET /api/text/:id (authenticated) or the CDN artifact, never by the
 * recommender.
 */
export interface ReadingCandidate {
  /** `text.id` (UUID) — the durable key; `int_id` stays internal to stats. */
  id: string;
  title: string | null;
  totalWordCount: number;
  knownWordCount: number;
  unknownWordCount: number;
  knownPercent: number;
  /** 100 at OPTIMAL_KNOWN_PERCENT, −2 per point of deviation, floor 0. */
  challengeScore: number;
  source: string | null;
  publication: PublicationMetadata | null;
}
